import React, { useEffect } from 'react';
import { useLocation, useModel, useIntl } from '@umijs/max';
import { KeepAlive } from 'react-activation';
import routes from '../../../config/routes';
import { getRouteInfo } from '@/utils/menu';
import { getAppName } from '@/utils/app-cache';
import { isStandaloneApp } from '@/utils/env';

/**
 * 将“同一业务实体”的不同子页面合并到同一个 Tab/KeepAlive 缓存里。
 *
 * 诉求：
 * - `/memory/memory-setting/:id` 与 `/memory/memory-message/:id` 归属同一个 tab
 * - 容器侧也可能出现类似路径：`/app/memory/memory-xxx/:id`、`/app/mgmt/memory/memory-xxx/:id`
 */
const normalizePathnameForTabKey = (pathname: string) => {
  // 统一 memory 的两个子页面：memory-message / memory-setting
  // 允许三种前缀：/memory、/app/memory、/app/mgmt/memory
  const m = pathname.match(
    /^(\/(?:memory|app\/memory|app\/mgmt\/memory))\/memory-(?:setting|message)\/([^/?#]+)(?:[/?#].*)?$/,
  );
  if (m) {
    const base = m[1];
    const id = m[2];
    return `${base}/memory/${id}`;
  }
  // 统一 dataset 的两个子页面：dataset-overview / dataset（同一数据集 ID 归属同一 tab）
  const d = pathname.match(
    /^\/data\/dataset\/(dataset-overview|dataset|dataset-setting|testing)\/([^/?#]+)(?:[/?#].*)?$/,
  );
  if (d) {
    const id = d[2];
    return `/data/dataset/dataset/${id}`;
  }
  // 统一 /app/mgmt/:appType/:id/* 的 micro-app 内部子路由归属同一 tab
  // 排除 list / analysis / memory（memory 已在上方单独处理）
  const mgmtMicro = pathname.match(
    /^(\/app\/mgmt\/(?!(?:list|analysis|memory)\b)[^/]+\/[^/?#]+)\/[^/?#].*/,
  );
  if (mgmtMicro) {
    return mgmtMicro[1];
  }
  // 统一 /app/:appType/:id/* 的 micro-app 内部子路由（非 mgmt 前缀）
  const appMicro = pathname.match(
    /^(\/app\/(?!(?:mgmt|mine)\b)[^/]+\/[^/?#]+)\/[^/?#].*/,
  );
  if (appMicro) {
    return appMicro[1];
  }
  return pathname;
};

/**
 * 从 pathname 解析 thirdpartyAppId，用于读 localStorage 缓存的应用名。
 * 支持的 path 格式：
 * - /app/mgmt/memory/memory-message/:id
 * - /app/mgmt/:appType/:id[/*]        ← agent / next-chat / next-search 等
 * - /app/memory/memory-message/:id
 * - /app/:appType/:id[/*]
 * - /data/dataset/:pageType/:id
 *
 * 注意：必须从最具体到最通用排序，避免 /app/ 提前匹配 /app/mgmt/ 等路径。
 * idIndex 指 pathname.split('/').filter(Boolean) 数组中 thirdpartyAppId 所在的下标。
 */
const PREFIXES_WITH_APP_ID: { prefix: string; idIndex: number }[] = [
  { prefix: '/app/mgmt/memory/', idIndex: 4 }, // ['app','mgmt','memory','memory-message', id]
  { prefix: '/app/mgmt/', idIndex: 3 },         // ['app','mgmt', appType, id, ...]
  { prefix: '/app/memory/', idIndex: 3 },       // ['app','memory','memory-message', id]
  { prefix: '/app/', idIndex: 2 },              // ['app', appType, id, ...]
  { prefix: '/data/dataset/', idIndex: 3 },     // ['data','dataset', pageType, id]
];

/**
 * 从统一缓存读取应用名（用于刷新后恢复 tab 标题）
 * 列表页跳转时已写入：setAppName(thirdpartyAppId, appName)
 */
const getCachedAppNameByPathname = (pathname: string, search?: string): string | null => {
  // 对于 /data/chunk/ 路径，doc_id 在 query 参数中
  if (pathname.startsWith('/data/chunk/') && search) {
    const params = new URLSearchParams(search);
    const docId = params.get('doc_id');
    if (docId) {
      return getAppName(docId);
    }
  }

  const entry = PREFIXES_WITH_APP_ID.find((p) => pathname.startsWith(p.prefix));
  if (!entry) return null;
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length <= entry.idIndex) return null;
  const thirdpartyAppId = parts[entry.idIndex];
  if (!thirdpartyAppId) return null;
  return getAppName(thirdpartyAppId);
};

/**
 * 计算 KeepAlive 的 name
 * 优先使用配置的 keepAliveName，其次是 routeInfo.key，最后兜底使用 pathname
 */
const getKeepAliveName = (pathname: string, routeInfo: any) => {
  // 默认兜底时也要走一次归一化，否则会出现同一实体被拆成多个 tab
  let keepAliveName = normalizePathnameForTabKey(pathname);
  if (routeInfo) {
    if (routeInfo.keepAliveName) {
      keepAliveName = routeInfo.keepAliveName;
    } else if (routeInfo.name) {
      // 如果是动态路由，使用 name 会导致冲突，这里做一个简单判断
      // 如果 pathPattern (route.path) 包含 :，则依然使用 pathname
      // 否则使用 routeInfo.key (如 menu.system.user) 以确保唯一性
      if (routeInfo.pathPattern && routeInfo.pathPattern.includes(':')) {
        keepAliveName = normalizePathnameForTabKey(pathname);
      } else {
        // 使用 key 而不是 name，避免不同模块下的同名路由（如 list）冲突
        keepAliveName = routeInfo.key || routeInfo.name;
      }
    }
  }
  return keepAliveName;
};

const getKeepAliveConfig = (isStandalone: boolean) => {
  return {
    fixedTabs: isStandalone ? ['/app/mine'] : ['/dashboard/workplace'],
    unclosableTabs: isStandalone
      ? ['/', '/app/mine']
      : ['/', '/dashboard/workplace'],
  };
};

const KeepAliveWrapper: React.FC<{ children: React.ReactNode }> = (props) => {
  const location = useLocation();
  const { addTab, setActiveKey } = useModel('system');
  const intl = useIntl();

  const { pathname, search } = location;
  const routeInfo = getRouteInfo(pathname, routes);

  const keepAliveName = getKeepAliveName(pathname, routeInfo);

  // 是否开启缓存，默认为 true
  // 只有显式设置为 false 才禁用，否则默认开启（只要在 KeepAliveWrapper 中）
  const isKeepAlive = routeInfo?.keepAlive !== false;

  // 跳转时通过 state 传入的自定义 tab 标题（如应用管理列表跳转时传入 appName）
  const customTabTitle = (location.state as { tabTitle?: string } | null)
    ?.tabTitle;

  // 初始化时确保固定 Tab 存在
  useEffect(() => {
    const isStandalone = isStandaloneApp();
    const config = getKeepAliveConfig(isStandalone);
    
    config.fixedTabs.forEach((path) => {
      const targetRouteInfo = getRouteInfo(path, routes);

      if (targetRouteInfo && targetRouteInfo.name) {
        let title = targetRouteInfo.name;
        // 处理国际化标题
        const localeTitle = intl.formatMessage({
          id: targetRouteInfo.key,
          defaultMessage: targetRouteInfo.name,
        });
        if (localeTitle && localeTitle !== targetRouteInfo.key) {
          title = localeTitle;
        }

        const kaName = getKeepAliveName(path, targetRouteInfo);

        addTab({
          key: kaName, // Use keepAliveName as key
          pathname: path,
          title,
          closable: false,
          keepAliveName: kaName,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 过滤掉无效路由：
    // 1. 没有路由信息
    // 2. 没有 name (通常是重定向路由或布局容器)
    // 3. 显式隐藏的菜单 (hideInMenu) - 视需求而定，有些 hideInMenu 的页面也需要 Tab，比如详情页，所以这里暂不按 hideInMenu 过滤
    // 4. 有子路由且没有组件的路由（通常是目录）
    // 5. 有 redirect 属性的路由
    if (
      !routeInfo ||
      !routeInfo.name ||
      (routeInfo.hasChildren && !routeInfo.component) ||
      routeInfo.redirect
    ) {
      return;
    }

    let title = routeInfo.name;
    let closable = true;

    // 优先使用跳转时通过 state 传入的自定义标题（如应用配置页用 appName 作为 tab 标题）
    if (customTabTitle && customTabTitle.trim()) {
      title = customTabTitle.trim();
    } else {
      // 刷新页面时 state 会丢失，从 localStorage 读取应用名缓存（仅应用管理详情页）
      const cachedAppName = getCachedAppNameByPathname(pathname, search);
      if (cachedAppName) {
        title = cachedAppName;
      } else {
        // 处理国际化标题
        const localeTitle = intl.formatMessage({
          id: routeInfo.key,
          defaultMessage: routeInfo.name,
        });
        // 这里的 routeInfo.key 是 menu.xxx 格式，用于国际化
        if (localeTitle && localeTitle !== routeInfo.key) {
          title = localeTitle;
        }
      }
    }

    // users 知识库的 tab 标题(无论来自 state、缓存还是路由)统一显示为「个人知识库」
    if (title === 'users' && pathname.startsWith('/data/dataset/')) {
      title = intl.formatMessage({ id: 'data.dataset.personalName' });
    }

    // 根据配置判断是否可关闭
    const isStandalone = isStandaloneApp();
    const config = getKeepAliveConfig(isStandalone);
    if (config.unclosableTabs.includes(pathname)) {
      closable = false;
    }

    addTab({
      key: keepAliveName, // Use keepAliveName as key
      pathname: pathname + search,
      title,
      closable,
      keepAliveName,
    });
    setActiveKey(keepAliveName); // Use keepAliveName as key
  }, [
    pathname,
    search,
    addTab,
    setActiveKey,
    intl,
    routeInfo,
    keepAliveName,
    customTabTitle,
  ]);
  // 开发环境禁用 KeepAlive，保证 HMR 正常生效
  if (process.env.NODE_ENV === 'development') {
    return <>{props.children}</>;
  }

  return (
    <KeepAlive
      name={keepAliveName}
      when={isKeepAlive}
      saveScrollPosition="screen"
    >
      {props.children}
    </KeepAlive>
  );
};

export default KeepAliveWrapper;
